import { Tag, Typography, Space, Empty, Button, Collapse } from "antd";
import { clsx } from "clsx";

import { RVIcon, RVIconSizeOptions } from "@regal-voice/ui";
import { JourneyExecution, JourneyNode } from "Services/Journeys.service";

import { formatDate, getStatusKey, getStatusLabel } from "./helpers";

import styles from "./DetailView.module.scss";

const { Text } = Typography;
const { Panel } = Collapse;

function JourneyMonitorNodeValue({ value }: { value: unknown }): JSX.Element {
    if (value === null || value === undefined) {
        return <Text type="secondary">—</Text>;
    }
    if (typeof value === "object") {
        return <pre className={styles.jsonValue}>{JSON.stringify(value, null, 2)}</pre>;
    }
    return <Text>{String(value)}</Text>;
}

type DetailViewProps = {
    selectedExecution: JourneyExecution | null;
    selectedProfileName: string;
    loadingNodes: boolean;
    executionNodes: JourneyNode[];
    handleNodeClick: (journeyNodeFriendlyId: number | null) => void;
};

export function DetailView({
    selectedExecution,
    selectedProfileName,
    loadingNodes,
    executionNodes,
    handleNodeClick,
}: DetailViewProps): JSX.Element {
    const displayName = selectedProfileName || selectedExecution?.executionName || "";

    return (
        <>
            {selectedExecution && (
                <div className={styles.executionDetail}>
                    <div className={styles.detailHeaderInfo}>
                        <div className={styles.detailHeaderRow}>
                            <div className={styles.detailHeaderContent}>
                                <Text className={styles.detailTitle} ellipsis={{ tooltip: displayName }}>
                                    {displayName}
                                </Text>
                                <Text type="secondary" className={styles.detailDate}>
                                    Started: {formatDate(selectedExecution.startedAt)}
                                </Text>
                            </div>
                            <span
                                className={clsx(
                                    styles.statusTag,
                                    styles[`statusTag_${getStatusKey(selectedExecution.status)}`]
                                )}
                            >
                                {getStatusLabel(selectedExecution.status)}
                            </span>
                        </div>
                    </div>

                    {loadingNodes ? (
                        <div className={styles.loadingState}>
                            <Text type="secondary">Loading execution history...</Text>
                        </div>
                    ) : executionNodes.length > 0 ? (
                        <div className={styles.nodesList}>
                            <Text strong className={styles.listTitle}>
                                Execution History ({executionNodes.length} nodes)
                            </Text>
                            <Collapse accordion className={styles.nodesCollapse}>
                                {executionNodes.map((node, index) => (
                                    <Panel
                                        header={
                                            <div className={styles.nodeHeader}>
                                                <div className={styles.nodeHeaderText}>
                                                    <Text strong>
                                                        {node.journeyNodeFriendlyId !== null &&
                                                        node.journeyNodeFriendlyId !== undefined
                                                            ? `#${node.journeyNodeFriendlyId} - ${node.nodeName}`
                                                            : node.nodeName}
                                                    </Text>
                                                    {node.nodeType && <Tag>{node.nodeType}</Tag>}
                                                </div>
                                                {node.journeyNodeFriendlyId !== null &&
                                                    node.journeyNodeFriendlyId !== undefined && (
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={
                                                                <RVIcon
                                                                    icon="bullseye-arrow"
                                                                    size={RVIconSizeOptions.SMALL}
                                                                />
                                                            }
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleNodeClick(node.journeyNodeFriendlyId);
                                                            }}
                                                            title="Center on node in editor"
                                                            className={styles.centerButton}
                                                        />
                                                    )}
                                            </div>
                                        }
                                        key={index}
                                    >
                                        <Space direction="vertical" size="small" className={styles.fullWidth}>
                                            {node.journeyNodeFriendlyId !== null &&
                                                node.journeyNodeFriendlyId !== undefined && (
                                                    <div>
                                                        <Text type="secondary">Friendly ID: </Text>
                                                        <Text>{node.journeyNodeFriendlyId}</Text>
                                                    </div>
                                                )}
                                            <div>
                                                <Text type="secondary">Node Name: </Text>
                                                <Text>{node.nodeName}</Text>
                                            </div>
                                            {node.startedAt && (
                                                <div>
                                                    <Text type="secondary">Started: </Text>
                                                    <Text>{formatDate(node.startedAt)}</Text>
                                                </div>
                                            )}
                                            {node.completedAt && (
                                                <div>
                                                    <Text type="secondary">Completed: </Text>
                                                    <Text>{formatDate(node.completedAt)}</Text>
                                                </div>
                                            )}
                                            {node.nextStep && (
                                                <div>
                                                    <Text type="secondary">Next Step: </Text>
                                                    <Text>{node.nextStep}</Text>
                                                </div>
                                            )}
                                            {node.stepCounter !== null && (
                                                <div>
                                                    <Text type="secondary">Step Counter: </Text>
                                                    <Text>{node.stepCounter}</Text>
                                                </div>
                                            )}
                                            {!!node.nodeOutput && (
                                                <div>
                                                    <Text type="secondary">Node Output:</Text>
                                                    <JourneyMonitorNodeValue value={node.nodeOutput} />
                                                </div>
                                            )}
                                        </Space>
                                    </Panel>
                                ))}
                            </Collapse>
                        </div>
                    ) : (
                        <div className={styles.emptyState}>
                            <Empty
                                description="No nodes found for this execution"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
